import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import get from 'lodash.get';
import { Box, FlexBox, Grid, Icon, MuiBox, IconButton, Typography, color } from '@kis-technologies/kis-ui';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { Scrollbar } from 'components/Scrollbar';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, Cart } from 'services/cart';
import { selectors as configSelectors, LanguageField, MembershipSalesSettings, Messages } from 'services/configuration';
import {
  actions as ticketingActions,
  selectors as ticketingSelectors,
  MembershipLevel,
  UpsellStore
} from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { useStyles } from './styled';

const MembershipLevelSelectionView = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const currencyConfig = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  const { getMembershipProduct, resetMembershipProduct, setMembershipProductConfig } = useActions({
    getMembershipProduct: ticketingActions.getMembershipProduct,
    resetMembershipProduct: ticketingActions.resetMembershipProduct,
    setMembershipProductConfig: ticketingActions.setMembershipProductConfig
  });

  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const configuration = useSelector(configSelectors.selectConfiguration);
  const fetchCompleted = useSelector(ticketingSelectors.selectFetchCompleted);
  const membershipProduct = useSelector(ticketingSelectors.selectMembershipProduct);
  const { selected } = useSelector(ticketingSelectors.selectMembershipProductConfig);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);
  const upsell = useSelector(ticketingSelectors.selectUpsell) as UpsellStore;
  const upsellProducts = useSelector(ticketingSelectors.selectUpsellProducts);

  const cartLabel = get(configuration, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary', '');
  const primaryPalette = get(configuration, 'configuration.globalSettings.palette.primary');
  const cartEnabled = get(configuration, `${ticketingPath}.cart.enabled`, false);
  const membershipSalesSettingsList: MembershipSalesSettings[] = get(
    configuration,
    'ticketing.membershipSalesSettingsList',
    []
  );
  const upsellMessages: Messages = get(
    configuration,
    `${ticketingPath}.productAvailabilityAndSelection.upsell.membershipLevelSelectionMessages`,
    {}
  );
  const upsellHeader = get(upsellMessages, `header.${i18n.language}`, '');
  const upsellSubheader = get(upsellMessages, `subheader.${i18n.language}`, '');
  const upsellBackText = get(upsellMessages, `buttons.${i18n.language}[0]`, t('commonText.back'));

  const [uiLoading, setUILoading] = React.useState(true);
  const [selectedLevel, setSelectedLevel] = React.useState<string>(selected || '');
  const [membershipProductSettings, setMembershipProductSettings] = React.useState<any>(null);

  const classes = useStyles({ fontFamily })();

  // Get the membership details
  React.useEffect(() => {
    if (!membershipProduct) getMembershipProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get the related config and hide the loading overlay after the API call
  React.useEffect(() => {
    if (fetchCompleted.membershipProduct) {
      if (membershipProduct) {
        setMembershipProductSettings(membershipSalesSettingsList.find((mss) => mss.name === membershipProduct.name));
      }
      setUILoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipProduct, fetchCompleted.membershipProduct]);

  // Navigate back
  const onPrevPageClick = () => {
    setMembershipProductConfig({ selected: '' });
    resetMembershipProduct();

    // re-routes to the previous page (EventsView or UpsellSelectionView)
    const prevPage = upsell.initialProduct.details ? '/ticketing/upsell-selection' : '/ticketing/events';
    navigate(prevPage, { state: { from: location.pathname } });
  };

  // Navigate forward
  const onNextPageClick = () => {
    setMembershipProductConfig({ selected: selectedLevel });

    // re-routes to the next page (MembershipDataCollection)
    navigate(`/ticketing/membership-data-collection`);
  };

  // Set the tile as selected/deselected when clicked
  const onTileClick = (level: MembershipLevel) => {
    if (selectedLevel && selectedLevel === level.name) {
      setSelectedLevel('');
    } else {
      setSelectedLevel(level.name);
    }
  };

  const getHeaderText = () => {
    if (upsellProducts.length && upsellHeader) {
      return upsellHeader;
    }

    return get(
      membershipProductSettings,
      `levelSelection.messages.header.${i18n.language}`,
      t('membershipLevelSelection.header')
    );
  };

  const getSubheaderText = () => {
    if (upsellProducts.length && upsellSubheader) {
      return upsellSubheader;
    }

    return get(
      membershipProductSettings,
      `levelSelection.messages.subheader.${i18n.language}`,
      t('membershipLevelSelection.subheader')
    );
  };

  const getBackwardNavButtonText = () => {
    if (upsellProducts.length && upsellBackText) {
      return upsellBackText;
    }

    return t('commonText.back');
  };

  const getForwardNavButtonText = () => {
    if (!selectedLevel && upsellProducts.length === 1 && cart) {
      const configuredUpsellText = get(upsellMessages, `buttons.${i18n.language}[1]`);
      if (configuredUpsellText) return configuredUpsellText;

      if (cartEnabled) return t('cartSummary.proceedToCart', { cartLabel });
    }

    return t('commonText.next');
  };

  const isForwardNavButtonDisabled = () => {
    if (selectedLevel || (upsellProducts.length === 1 && cart)) {
      return false;
    }

    return true;
  };

  // Get the localized html string for the benefits of the specified level or "allLevels"
  const getConfiguredBenefitText = (key: string) => {
    const benefitText = get(membershipProductSettings, `benefits.${key}.${i18n.language}`);

    return benefitText;
  };
  const commonBenefits = getConfiguredBenefitText('allLevels');

  // Loading overlay until the membershipProduct is fetched
  if (uiLoading) return <LoadingOverlay />;

  return (
    <>
      <Grid container className={classes.outerContainer}>
        <Grid container className={classes.innerContainer}>
          {/* Title */}
          <MuiBox className={classes.headerContainer}>
            <Typography variant="h2">{getHeaderText()}</Typography>
            <Typography fontSize={30}>{getSubheaderText()}</Typography>
          </MuiBox>

          {membershipProduct && membershipProduct.levels && membershipProduct.levels.length ? (
            <>
              {/* Common benefits (left side or top) */}
              <Grid container direction="column" className={classes.commonBenefitsContainer}>
                {commonBenefits ? (
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(commonBenefits) }}
                    style={{ height: '100%', width: '100%' }}
                  />
                ) : (
                  <FlexBox height="100%" align="center">
                    <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
                  </FlexBox>
                )}
              </Grid>

              {/* Membership levels (right side or bottom) */}
              <Grid container direction="column" className={classes.membershipLevelsContainer}>
                <FlexBox style={{ minHeight: '26.5rem' }} direction="column" noWrap>
                  <Scrollbar refreshModel={selectedLevel}>
                    <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                      {/* Tile for each membership level */}
                      {membershipProduct.levels.map((level: MembershipLevel, idx) => {
                        const isSelected = selectedLevel === level.name;
                        const benefitText = getConfiguredBenefitText(level.name);

                        return (
                          <FlexBox
                            border={isSelected ? `3px solid ${primaryPalette}` : `solid 3px ${color.LIGHT_GRAY}`}
                            height="auto"
                            style={{ minHeight: '80px' }}
                            key={idx}
                          >
                            <FlexBox
                              padding="2 4"
                              align="center"
                              justify="between"
                              onClick={() => onTileClick(level)}
                              width="100%"
                            >
                              <IconButton
                                aria-label="expand row"
                                size="small"
                                style={{
                                  border: `3px solid ${primaryPalette}`,
                                  borderRadius: '50%',
                                  color: primaryPalette
                                }}
                              >
                                {isSelected ? (
                                  <Icon name="KeyboardArrowUp" fontSize="large" />
                                ) : (
                                  <Icon name="KeyboardArrowDown" fontSize="large" />
                                )}
                              </IconButton>
                              <Grid className={classes.membershipLevelName}>
                                <Typography variant="h4">{level.name}</Typography>
                              </Grid>
                              <Box width={2} margin="0 7 0 3" height={70} sx={{ backgroundColor: color.LIGHT_GRAY }} />
                              <FlexBox width={70} align="center" justify="end">
                                <Typography fontSize={24} fontWeight={700}>
                                  {formatCurrency(level.basePrice, currencyConfig, languageSettings)}
                                </Typography>
                              </FlexBox>
                            </FlexBox>
                            {isSelected && benefitText && (
                              <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(benefitText) }}
                                style={{ width: '100%', minHeight: '80px' }}
                              />
                            )}
                          </FlexBox>
                        );
                      })}
                    </FlexBox>
                  </Scrollbar>
                </FlexBox>
              </Grid>
            </>
          ) : (
            <>
              {/* No levels to show */}
              <FlexBox height="600px" align="center">
                <Typography fontSize={30}>{t('commonText.nothingToDisplay')}</Typography>
              </FlexBox>
            </>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Footer
        onPrevClick={onPrevPageClick}
        nextText={getForwardNavButtonText()}
        onNextClick={onNextPageClick}
        isNextDisabled={isForwardNavButtonDisabled()}
        prevText={getBackwardNavButtonText()}
      />
    </>
  );
};

export default MembershipLevelSelectionView;
